import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { getProjectRepository } from "../../data/projectRepository";

/**
 * 读取当前项目名称。queryKey 与 ProjectHome 保持一致，
 * 同一项目页内不会触发重复请求。
 */
export function useProjectName(projectId: string): string | undefined {
  const project = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => (await getProjectRepository()).read(projectId)
  });
  return project.data?.name;
}

interface ContextHeaderProps {
  projectId: string;
  /** 小标签，默认显示项目名称 */
  eyebrow?: string;
  title: string;
  description?: string;
  /** 右侧动作槽 */
  actions?: ReactNode;
  /** 可选的返回链接 */
  backTo?: { href: string; label: string };
}

/**
 * A 方向页内上下文头：说明“这是哪个项目、当前在做什么”，
 * 右侧预留主操作位置。结构对应 styles.css 的 .context-head 系列。
 */
export function ContextHeader({
  projectId,
  eyebrow,
  title,
  description,
  actions,
  backTo
}: ContextHeaderProps): React.JSX.Element {
  const projectName = useProjectName(projectId);
  return (
    <header className="context-head">
      <div className="context-head-main">
        {backTo ? (
          <a className="context-back" href={backTo.href}>
            ← {backTo.label}
          </a>
        ) : null}
        <p className="context-eyebrow">{eyebrow ?? projectName ?? "课表项目"}</p>
        <h1 className="context-title">{title}</h1>
        {description ? <p className="context-desc">{description}</p> : null}
      </div>
      {actions ? <div className="context-actions">{actions}</div> : null}
    </header>
  );
}
